import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { ArrowLeft, AlertCircle } from "lucide-react";
import { usePaymentApi } from "../hooks/use-payment-api";
import paymentService from "../../../../services/paymentService";
import billingService from "../../../../services/billingService";
import { PhoneInput } from "../../../../shadcn/ui/phone-input";
import { Input } from "../../../../shadcn/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../shadcn/ui/select";
import { Button } from "../../../../shadcn/ui/button";
import { Label } from "../../../../shadcn/ui/label";
import { Checkbox } from "../../../../shadcn/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../../shadcn/ui/tooltip";
import messageHandler from "../../../../utils/messageHandler";
import { getUser } from "../../../../services/localService";
import apiV2Service from "../../../../services/apiV2Service";
import { useSelector } from "react-redux";
import logger from "../../../../utils/logger";


// Custom SelectItem with tooltip for disabled items
const SelectItemWithTooltip = ({ children, disabled, value, ...props }) => {
  if (disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <SelectItem
            value={value}
            disabled={disabled}
            className="!pointer-events-auto cursor-not-allowed"
            style={{ pointerEvents: "auto" }}
            {...props}
          >
            {children}
          </SelectItem>
        </TooltipTrigger>
        <TooltipContent>
          <p>Coming soon</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <SelectItem value={value} disabled={disabled} {...props}>
      {children}
    </SelectItem>
  );
};

const PaymentSetupForm = ({ onPrevious, onComplete, existingData = null, borderless = false }) => {
  const { loading, error, submitPaymentSetup } = usePaymentApi();
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [countriesError, setCountriesError] = useState(null);
  const [paymentInstitutions, setPaymentInstitutions] = useState([]);
  const [paymentInstitutionsLoading, setPaymentInstitutionsLoading] = useState(false);
  const [paymentInstitutionsError, setPaymentInstitutionsError] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(false);
  const [currenciesError, setCurrenciesError] = useState(null);
  const [isPrefilling, setIsPrefilling] = useState(false); // Flag to prevent infinite loops
  const loadingInstitutionsRef = useRef(false); // Ref to track loading state without causing re-renders
  const userSelectedCountryRef = useRef(false); // Tracks whether user manually picked a country

  // Spacia's countries from Redux (same source the Settings screen uses)
  const spaciaCountriesFromRedux = useSelector(state => state.dashboard?.countries || []);

  // Org country name from API — used as secondary fallback
  const [orgCountryName, setOrgCountryName] = useState(null);

  // Extract data from existingData or setupData
  const getExistingValue = useCallback(
    (field, fallback = "") => {
      return existingData?.[field] || existingData?.setupData?.[field] || existingData?.details?.[field] || fallback;
    },
    [existingData]
  );

  // Helper to get initial country value
  const getInitialCountry = () => {
    if (!existingData) return "";
    const countryValue = getExistingValue("country") || getExistingValue("countryCode") || "";
    return countryValue;
  };

  const safeCountries = useMemo(() => (Array.isArray(countries) ? countries : []), [countries]);
  const safePaymentInstitutions = useMemo(
    () => (Array.isArray(paymentInstitutions) ? paymentInstitutions : []),
    [paymentInstitutions]
  );
  const safeCurrencies = useMemo(() => (Array.isArray(currencies) ? currencies : []), [currencies]);
  const countriesCount = safeCountries.length;
  const paymentInstitutionsCount = safePaymentInstitutions.length;
  const currenciesCount = safeCurrencies.length;
  const hasCountries = countriesCount > 0;
  const hasPaymentInstitutions = paymentInstitutionsCount > 0;
  const hasCurrencies = currenciesCount > 0;

  // Fetch organization details from API to get country name
  useEffect(() => {
    const fetchOrgCountry = async () => {
      try {
        const user = getUser();
        const orgId = user?.organizationId || user?.organization?.id;
        if (!orgId) return;

        const response = await apiV2Service.getOrganizationById(orgId);
        const orgData = response?.data?.data || response?.data || {};
        // The API response may have country as a name string or as an object
        const country =
          orgData?.countryName || orgData?.country?.name || orgData?.country || orgData?.address?.country || null;
        if (country) {
          setOrgCountryName(country);
        }
      } catch (err) {
        logger.error("Failed to fetch org country:", err);
      }
    };
    fetchOrgCountry();
  }, []);

  // Find the matching Paystack country for this organization.
  //
  // Strategy (mirrors exactly what the Settings screen does):
  //   PRIMARY — Use Spacia's internal countryId from the user object, look it up in
  //             Spacia's own countries list (Redux), get the ISO-3166 code ("KE", "GH", ...),
  //             then find the Paystack country with the same iso_code.
  //             ISO codes are universal, so this correctly bridges the two ID systems
  //             without ever comparing Spacia IDs against Paystack IDs.
  //   SECONDARY — Fall back to matching by country name from the org API.
  //   NO timezone fallback — removed; it matched the developer's machine, not the org.
  const matchedCountry = useMemo(() => {
    if (!hasCountries) return null;

    // PRIMARY: Spacia countryId → Redux Spacia countries → ISO code → Paystack country
    const currentUser = getUser();
    const spaciaCountryId = currentUser?.countryId || currentUser?.organization?.countryId;
    if (spaciaCountryId && spaciaCountriesFromRedux.length > 0) {
      const spaciaCountry = spaciaCountriesFromRedux.find(c => c.id === spaciaCountryId);
      if (spaciaCountry?.code) {
        const byIsoCode = safeCountries.find(
          c => (c.iso_code || c.isoCode || c.code)?.toUpperCase() === spaciaCountry.code.toUpperCase()
        );
        if (byIsoCode) return byIsoCode;
      }
    }

    // SECONDARY: Match by country name from org API (async, arrives later)
    if (orgCountryName && typeof orgCountryName === "string") {
      const byName = safeCountries.find(c => c.name?.toLowerCase() === orgCountryName.toLowerCase());
      if (byName) return byName;
    }

    return null;
  }, [safeCountries, hasCountries, orgCountryName, spaciaCountriesFromRedux]);

  // TRANSFORM COUNTRIES INTO PAIRS (Nigeria - NGN, Nigeria - USD, etc.)
  // Filtered to only show the organization's country
  const countryCurrencyPairs = useMemo(() => {
    if (!hasCountries) return [];

    const pairs = [];
    safeCountries.forEach(country => {
      // Only show the matched country (if we found one)
      if (matchedCountry && country.id?.toString() !== matchedCountry.id?.toString()) return;

      const countryName = country.name;
      const countryCode = country.iso_code || country.isoCode || country.code;

      // Get the country's official/default currency
      const defaultCurr =
        country.default_currency_code || country.defaultCurrencyCode || country.currency_code || country.currencyCode;

      if (defaultCurr) {
        pairs.push({
          id: `${countryCode}-${defaultCurr}`,
          countryName,
          countryCode,
          currencyCode: defaultCurr,
          label: `${countryName} (${defaultCurr})`,
        });
      }
    });
    return pairs;
  }, [safeCountries, hasCountries, matchedCountry]);

  // Load countries directly from API
  const loadCountries = async () => {
    if (countriesLoading || hasCountries) return;

    try {
      setCountriesLoading(true);
      setCountriesError(null);

      const response = await paymentService.getCountries();

      // API returns: {"data": [...]}
      // After interceptor: {data: {"data": [...]}}
      // So we need: response.data.data
      if (response && response.data && Array.isArray(response.data.data)) {
        setCountries(response.data.data);
      } else if (response && response.data && Array.isArray(response.data)) {
        // Fallback: in case response structure is different
        setCountries(response.data);
      } else {
        throw new Error("No countries data received");
      }
    } catch (error) {
      logger.error("Error loading countries:", error);
      setCountriesError(error.message || "Failed to load countries");
    } finally {
      setCountriesLoading(false);
    }
  };

  const findCountryDetails = useCallback(
    value => {
      if (!value) return null;
      const target = value.toString().toLowerCase();
      return safeCountries.find(country => {
        if (!country) return false;
        const possibleMatches = [
          country.name,
          country.iso_code,
          country.isoCode,
          country.code,
          country.countryCode,
          country.id?.toString(),
        ].filter(Boolean);
        return possibleMatches.some(match => match.toString().toLowerCase() === target);
      });
    },
    [safeCountries]
  );

  const getCountryCurrencyCode = useCallback(
    value => {
      const countryDetails = findCountryDetails(value);
      if (!countryDetails) return "";
      return (
        countryDetails.default_currency_code ||
        countryDetails.defaultCurrencyCode ||
        countryDetails.currency_code ||
        countryDetails.currencyCode ||
        countryDetails.currency?.code ||
        ""
      );
    },
    [findCountryDetails]
  );

  // Load payment institutions directly from API
  const loadPaymentInstitutions = useCallback(async (currencyCode, countryCode) => {
    // Prevent multiple simultaneous requests
    if (loadingInstitutionsRef.current) {
      return;
    }

    // API requires currency parameter - validate it's provided
    if (!currencyCode) {
      logger.error("Currency parameter is required for loading payment institutions");
      setPaymentInstitutionsError("Currency is required");
      return;
    }

    loadingInstitutionsRef.current = true;
    setPaymentInstitutionsLoading(true);
    setPaymentInstitutionsError(null);

    try {
      const response = await paymentService.getPaymentInstitutions(currencyCode, countryCode);

      // API returns: {"data": {"data": [...], "meta": {...}}}
      // After interceptor: {data: {"data": {"data": [...], "meta": {...}}}}
      // So we need: response.data.data.data
      if (response && response.data && response.data.data && Array.isArray(response.data.data.data)) {
        setPaymentInstitutions(response.data.data.data);
      } else if (response && response.data && Array.isArray(response.data.data)) {
        // Fallback: in case response structure is different
        setPaymentInstitutions(response.data.data);
      } else {
        throw new Error("No payment institutions data received");
      }
    } catch (error) {
      logger.error("Error loading payment institutions:", error);
      setPaymentInstitutionsError(error.message || "Failed to load payment institutions");
    } finally {
      setPaymentInstitutionsLoading(false);
      loadingInstitutionsRef.current = false;
    }
  }, []); // No dependencies - stable function

  const [selectedCountry, setSelectedCountry] = useState(getInitialCountry());
  const [accountNumberError, setAccountNumberError] = useState("");

  // Payment Institution Types from API (GET /api/v1/payment-institution-types)
  const [paymentInstitutionTypes, setPaymentInstitutionTypes] = useState([]);
  const [paymentInstitutionTypesLoading, setPaymentInstitutionTypesLoading] = useState(false);
  const [selectedInstitutionType, setSelectedInstitutionType] = useState(null);

  const loadPaymentInstitutionTypes = useCallback(async (countryNameOrCode) => {
    if (!countryNameOrCode) {
      setPaymentInstitutionTypes([]);
      setSelectedInstitutionType(null);
      return;
    }
    const countryObj = findCountryDetails(countryNameOrCode);
    const countryCode =
      countryObj?.iso_code ||
      countryObj?.isoCode ||
      countryObj?.code ||
      countryObj?.countryCode ||
      (typeof countryNameOrCode === "string" && countryNameOrCode.length === 2 ? countryNameOrCode : null);

    if (!countryCode) return;

    setPaymentInstitutionTypesLoading(true);
    try {
      const response = await paymentService.getPaymentInstitutionTypes({
        countryCode: countryCode.toUpperCase(),
        limit: 50,
      });
      const types = response?.data || [];
      setPaymentInstitutionTypes(types);

      if (types.length > 0) {
        setSelectedInstitutionType(prev => {
          if (prev && types.some(t => t.id === prev.id || t.code?.toLowerCase() === prev.code?.toLowerCase())) {
            return types.find(t => t.id === prev.id || t.code?.toLowerCase() === prev.code?.toLowerCase());
          }
          return types[0];
        });
      } else {
        setSelectedInstitutionType(null);
      }
    } catch (err) {
      logger.error("Error loading payment institution types:", err);
      setPaymentInstitutionTypes([]);
      setSelectedInstitutionType(null);
    } finally {
      setPaymentInstitutionTypesLoading(false);
    }
  }, [findCountryDetails]);

  // Determine payment method from existing data
  const getInitialPaymentMethod = () => {
    if (existingData?.paymentMethod) {
      const pm = existingData.paymentMethod.toUpperCase();
      if (pm === "M_PESA_PAYBILL" || pm === "M-PESA_PAYBILL") return "paybill";
      if (pm === "M_PESA_TILL_NUMBER" || pm === "M-PESA_TILL") return "till";
      if (pm === "BANK") return "bank";
    }
    if (existingData?.type) {
      if (existingData.type === "paybill") return "paybill";
      if (existingData.type === "till") return "till";
      if (existingData.type === "bank") return "bank";
    }
    return "bank";
  };

  const initialPaymentMethod = getInitialPaymentMethod();

  // Helper to get initial currency value
  const getInitialCurrency = () => {
    if (!existingData) return "";
    const currencyValue =
      getExistingValue("currency") || getExistingValue("currencyCode") || getExistingValue("currency_code") || "";
    return currencyValue;
  };

  const [formData, setFormData] = useState({
    paymentMethod: initialPaymentMethod,
    country: getExistingValue("country") || getExistingValue("countryCode") || "",
    currency: getInitialCurrency(),
    paymentInstitutionId:
      getExistingValue("paymentInstitutionId")?.toString() ||
      getExistingValue("paymentInstitutionCode")?.toString() ||
      "",
    accountNumber: getExistingValue("accountNumber") || "",
    businessName: getExistingValue("businessName") || "",
    description: getExistingValue("description") || "",
    primaryContactEmail: getExistingValue("primaryContactEmail") || "",
    primaryContactName: getExistingValue("primaryContactName") || "",
    primaryContactPhone: getExistingValue("primaryContactPhone") || "",
    // Primary payment method toggle
    primary: existingData?.primary !== undefined ? existingData.primary : true,
  });

  // Load currencies
  const loadCurrencies = async () => {
    if (currenciesLoading || hasCurrencies) return;

    try {
      setCurrenciesLoading(true);
      setCurrenciesError(null);

      const response = await billingService.getCurrencies({ limit: 500, active: true });

      // API returns: {"data": {"data": [...]}}
      // After interceptor: {data: {"data": {"data": [...]}}}
      // So we need: response.data.data.data
      if (response && response.data && response.data.data && Array.isArray(response.data.data.data)) {
        setCurrencies(response.data.data.data);
      } else if (response && response.data && Array.isArray(response.data.data)) {
        // Fallback: in case response structure is different
        setCurrencies(response.data.data);
      } else {
        throw new Error("No currencies data received");
      }
    } catch (error) {
      logger.error("Error loading currencies:", error);
      setCurrenciesError(error.message || "Failed to load currencies");
    } finally {
      setCurrenciesLoading(false);
    }
  };

  // Load countries and currencies on mount
  useEffect(() => {
    loadCountries();
    loadCurrencies();
  }, []);

  // Auto-select the organization's country whenever matchedCountry resolves or changes.
  // Uses userSelectedCountryRef to avoid overriding a manual selection.
  // This runs even if selectedCountry is already set so that a late-resolving orgCountryName
  // (from the async org API call) can correct an earlier timezone-based guess.
  useEffect(() => {
    // Never auto-select in edit mode or after user has manually chosen
    if (existingData || userSelectedCountryRef.current) return;
    // Nothing to apply yet
    if (!matchedCountry) return;
    // Already correct — nothing to do
    if (selectedCountry === matchedCountry.name) return;

    setSelectedCountry(matchedCountry.name);
    const defaultCurrencyCode =
      matchedCountry.default_currency_code ||
      matchedCountry.defaultCurrencyCode ||
      matchedCountry.currency_code ||
      matchedCountry.currencyCode ||
      "";
    const matchingCurrency = defaultCurrencyCode
      ? safeCurrencies.find(c => c?.code?.toUpperCase() === defaultCurrencyCode.toUpperCase())
      : null;
    setFormData(prev => ({
      ...prev,
      country: matchedCountry.name,
      countryCode: matchedCountry.iso_code || matchedCountry.isoCode || matchedCountry.code,
      currency: matchingCurrency?.code || defaultCurrencyCode || prev.currency,
    }));
  }, [matchedCountry, existingData, selectedCountry, safeCurrencies]);

  // Step 1: When countries are loaded, extract and match country from existingData (for edit mode)
  useEffect(() => {
    const matchCountry = async () => {
      if (!existingData || !hasCountries || countriesCount === 0 || selectedCountry || isPrefilling) {
        // Even if country is already selected, check if currency needs to be set
        if (selectedCountry && !formData.currency && hasCurrencies) {
          const currencyCode = getCountryCurrencyCode(selectedCountry);
          if (currencyCode) {
            setFormData(prev => ({ ...prev, currency: currencyCode }));
          }
        }
        return;
      }

      setIsPrefilling(true);

      // Get paymentInstitutionCode from existingData (this is what the API returns)
      const institutionCode =
        getExistingValue("paymentInstitutionCode") ||
        existingData?.paymentInstitutionCode ||
        existingData?.setupData?.paymentInstitutionCode ||
        "";

      // If we have a code but no country, we need to find the institution first
      // Since API requires country parameter, we'll try loading institutions for each country
      // until we find the matching institution code
      if (institutionCode && !selectedCountry) {
        // Try to get country from all possible fields first
        let countryValue =
          getExistingValue("country") ||
          getExistingValue("countryCode") ||
          existingData?.country ||
          existingData?.setupData?.country ||
          "";

        // If no country found, we need to search through countries to find the institution
        // Since API requires country parameter, we'll try loading institutions for each country
        // Optimize by trying common countries first (Kenya, Ghana, Nigeria, etc.)
        if (!countryValue && paymentInstitutionsCount === 0) {
          // Common countries to try first (based on typical usage)
          const commonCountryNames = ["Kenya", "Ghana", "Nigeria", "South Africa"];
          const sortedCountries = [
            ...safeCountries.filter(c => commonCountryNames.includes(c.name)),
            ...safeCountries.filter(c => !commonCountryNames.includes(c.name)),
          ];

          let foundCountry = null;
          for (const country of sortedCountries) {
            if (!country.name) continue;

            try {
              const currencyFilter =
                country.default_currency_code ||
                country.defaultCurrencyCode ||
                country.currency_code ||
                country.currency?.code ||
                "";
              const paystackCountry = country.name; // Use name field directly
              const response = await paymentService.getPaymentInstitutions(currencyFilter, paystackCountry);
              let institutions = [];
              if (response && response.data && response.data.data && Array.isArray(response.data.data.data)) {
                institutions = response.data.data.data;
              } else if (response && response.data && Array.isArray(response.data.data)) {
                institutions = response.data.data;
              }

              // Check if this country has the institution with matching code
              const institution = institutions.find(
                inst => inst.code === institutionCode || inst.code?.toString() === institutionCode?.toString()
              );

              if (institution) {
                foundCountry = country.name;
                // Set the institutions for this country
                setPaymentInstitutions(institutions);
                break;
              }
            } catch (error) {
              // Continue to next country if this one fails
              logger.warn(`Failed to load institutions for ${country.name}:`, error);
              continue;
            }
          }

          if (foundCountry) {
            setSelectedCountry(foundCountry);
            const currencyCode = getCountryCurrencyCode(foundCountry);
            setFormData(prev => ({
              ...prev,
              country: foundCountry,
              currency: currencyCode || prev.currency, // Auto-set currency from country if available
            }));
            setIsPrefilling(false);
            return;
          } else {
            logger.error("Could not find institution with code:", institutionCode);
            setIsPrefilling(false);
            return;
          }
        }

        // If institutions are already loaded, find by code and get country
        if (paymentInstitutionsCount > 0) {
          const institution = safePaymentInstitutions.find(
            inst => inst.code === institutionCode || inst.code?.toString() === institutionCode?.toString()
          );
          if (institution && institution.country) {
            countryValue = institution.country;
          }
        }

        if (countryValue) {
          // Match country
          const countryExists = safeCountries.find(
            c => c.name === countryValue || c.iso_code === countryValue || c.id?.toString() === countryValue
          );

          if (countryExists) {
            setSelectedCountry(countryExists.name);
            const currencyCode = getCountryCurrencyCode(countryExists.name);
            setFormData(prev => ({
              ...prev,
              country: countryExists.name,
              currency: currencyCode || prev.currency, // Auto-set currency from country if available
            }));
          }
        }
      }

      // If currency is still not set after country matching, try to set it from country
      if (selectedCountry && !formData.currency && hasCurrencies) {
        const currencyCode = getCountryCurrencyCode(selectedCountry);
        if (currencyCode) {
          setFormData(prev => ({ ...prev, currency: currencyCode }));
        }
      }

      setIsPrefilling(false);
    };

    matchCountry();
  }, [
    existingData,
    hasCountries,
    countriesCount,
    safeCountries,
    selectedCountry,
    paymentInstitutionsCount,
    safePaymentInstitutions,
    formData.currency,
    hasCurrencies,
    getCountryCurrencyCode,
    getExistingValue,
    isPrefilling,
  ]);

  // Step 2: Load payment institutions when currency is selected (but not during prefilling)
  useEffect(() => {
    if (formData.currency && formData.country && !loadingInstitutionsRef.current && !isPrefilling) {
      // Only clear and reload if we don't already have institutions for this currency/country
      // We'll check if the current institutions match the selected currency
      const hasInstitutionsForCurrency =
        paymentInstitutionsCount > 0 && safePaymentInstitutions.some(inst => inst.currency === formData.currency);

      if (!hasInstitutionsForCurrency) {
        loadPaymentInstitutions(formData.currency, formData.country);
      }
    }
  }, [
    formData.currency,
    formData.country,
    isPrefilling,
    paymentInstitutionsCount,
    safePaymentInstitutions,
    loadPaymentInstitutions,
  ]);

  // Load payment institution types dynamically for selected country (GET /api/v1/payment-institution-types?countryCode=...)
  useEffect(() => {
    const countryVal = selectedCountry || formData.country;
    if (countryVal) {
      loadPaymentInstitutionTypes(countryVal);
    }
  }, [selectedCountry, formData.country, loadPaymentInstitutionTypes]);

  // Step 3: When payment institutions are loaded, match by code and prefill paymentInstitutionId (for edit mode)
  useEffect(() => {
    if (existingData && hasPaymentInstitutions && paymentInstitutionsCount > 0 && !isPrefilling) {
      // Get paymentInstitutionCode from existingData (this is what the API returns)
      const institutionCode =
        getExistingValue("paymentInstitutionCode") ||
        existingData?.paymentInstitutionCode ||
        existingData?.setupData?.paymentInstitutionCode ||
        "";

      // Also try to get paymentInstitutionId as fallback
      const institutionIdValue =
        getExistingValue("paymentInstitutionId") ||
        existingData?.paymentInstitutionId ||
        existingData?.setupData?.paymentInstitutionId ||
        "";

      if (institutionCode || institutionIdValue) {
        // Try to find matching institution by code first (preferred), then by id
        const institutionExists = safePaymentInstitutions.find(
          inst =>
            (institutionCode &&
              (inst.code === institutionCode || inst.code?.toString() === institutionCode?.toString())) ||
            (institutionIdValue &&
              (inst.id.toString() === institutionIdValue.toString() || inst.id === institutionIdValue))
        );

        if (institutionExists && formData.paymentInstitutionId !== institutionExists.id.toString()) {
          setFormData(prev => ({
            ...prev,
            paymentInstitutionId: institutionExists.id.toString(),
          }));

          // If we found the institution but don't have a country yet, get it from the institution
          if (institutionExists.country && !selectedCountry && hasCountries) {
            const countryExists = safeCountries.find(
              c => c.name === institutionExists.country || c.iso_code === institutionExists.country
            );
            if (countryExists) {
              setSelectedCountry(countryExists.name);
              setFormData(prev => ({ ...prev, country: countryExists.name }));
            }
          }
        }
      }
    }
  }, [
    existingData,
    hasPaymentInstitutions,
    paymentInstitutionsCount,
    safePaymentInstitutions,
    formData.paymentInstitutionId,
    selectedCountry,
    hasCountries,
    safeCountries,
    isPrefilling,
    getExistingValue,
  ]);

  // Helper function to determine payment method type from institution
  const getPaymentMethodTypeFromInstitution = institution => {
    if (!institution) return "bank";

    const name = institution.name?.toLowerCase() || "";
    const type = institution.type?.toLowerCase() || "";
    const gateway = institution.gateway?.toLowerCase() || "";

    // Check if it's a wallet/mobile money type
    if (type === "wallet" || gateway.includes("mpesa") || gateway.includes("m-pesa")) {
      if (institution.pay_with_bank === false) {
        if (name.includes("paybill") || name.includes("pay bill")) {
          return "paybill";
        } else if (name.includes("till")) {
          return "till";
        }
        return "paybill";
      }
    }

    return "bank";
  };

  // Helper function to resolve backend-allowed paymentInstitutionType: 'nuban' | 'ghipss' | 'mobile_money' | 'basa'
  const resolvePaymentInstitutionType = (institution, paymentMethodType = "bank") => {
    const rawType = (
      institution?.paymentInstitutionType ||
      institution?.type ||
      institution?.paymentChannelType ||
      ""
    ).toLowerCase();

    const allowedTypes = ["nuban", "ghipss", "mobile_money", "basa"];

    // 1. Direct match if it's already one of the allowed types
    if (allowedTypes.includes(rawType)) {
      return rawType;
    }

    // 2. Check for mobile money / wallet / mpesa
    const gateway = (institution?.gateway || "").toLowerCase();
    const name = (institution?.name || "").toLowerCase();
    if (
      rawType === "mobile_money" ||
      rawType === "wallet" ||
      rawType.includes("momo") ||
      gateway.includes("mpesa") ||
      gateway.includes("m-pesa") ||
      name.includes("mpesa") ||
      name.includes("m-pesa") ||
      name.includes("airtel") ||
      name.includes("mtn") ||
      name.includes("tigo") ||
      name.includes("telecel") ||
      paymentMethodType === "paybill" ||
      paymentMethodType === "till"
    ) {
      return "mobile_money";
    }

    // 3. Fallback based on currency / country:
    // Nigeria (NGN) -> nuban
    // Ghana (GHS) -> ghipss
    // South Africa (ZAR) -> basa
    // Kenya (KES) / Others:
    // If institution.type is 'kepss' or Kenyan bank -> nuban (or basa/ghipss depending on bank standard)
    const currency = (institution?.currency || formData.currency || "").toUpperCase();
    const country = (institution?.country || formData.country || selectedCountry || "").toLowerCase();

    if (currency === "GHS" || country.includes("ghana")) {
      return "ghipss";
    }
    if (currency === "ZAR" || country.includes("south africa")) {
      return "basa";
    }
    if (currency === "KES" || country.includes("kenya") || rawType === "kepss") {
      // Kenya banks (e.g. Absa, KCB, kepss) map to nuban
      return "nuban";
    }

    // Default to nuban (standard bank account)
    return "nuban";
  };

  // Determine payment method type from selected institution
  const getPaymentMethodType = () => {
    if (!formData.paymentInstitutionId) return formData.paymentMethod || "bank";
    const institution = safePaymentInstitutions.find(inst => inst.id.toString() === formData.paymentInstitutionId);
    if (!institution) return formData.paymentMethod || "bank";

    return getPaymentMethodTypeFromInstitution(institution);
  };

  // Validate account number (alphanumeric, min/max length based on payment institution type API)
  const validateAccountNumber = useCallback(
    (accountNumber, institutionCode = null, institutionType = selectedInstitutionType) => {
      if (!accountNumber || accountNumber.trim() === "") {
        return { isValid: false, error: "Account number is required" };
      }

      // Check if alphanumeric (letters and numbers only)
      const alphanumericRegex = /^[a-zA-Z0-9]+$/;
      if (!alphanumericRegex.test(accountNumber)) {
        return { isValid: false, error: "Account number must contain only letters and numbers" };
      }

      // Determine min/max length: prioritize dynamic limits from paymentInstitutionType API
      const specialCodes = ["MPPAYBILL", "MPTILL"];
      const isSpecialCode = institutionCode && specialCodes.includes(institutionCode.toUpperCase());
      const minLength = institutionType?.minAccountNumberLength ?? (isSpecialCode ? 5 : 10);
      const maxLength = institutionType?.maxAccountNumberLength ?? null;

      // Check minimum length
      if (minLength && accountNumber.length < minLength) {
        return {
          isValid: false,
          error: `Account number must be at least ${minLength} characters (current: ${accountNumber.length})`,
        };
      }

      // Check maximum length
      if (maxLength && accountNumber.length > maxLength) {
        return {
          isValid: false,
          error: `Account number cannot exceed ${maxLength} characters (current: ${accountNumber.length})`,
        };
      }

      return { isValid: true, error: null };
    },
    [selectedInstitutionType]
  );

  const handleSubmit = async () => {
    try {
      const _paymentMethodType = getPaymentMethodType();

      // Get selected institution to get its ID
      const selectedInstitution = safePaymentInstitutions.find(
        inst => inst.id.toString() === formData.paymentInstitutionId
      );

      if (!selectedInstitution) {
        messageHandler.showApiMessage({ message: "Please select a payment institution" }, "validation");
        return;
      }

      // Get the institution code (required for API)
      const institutionCode = selectedInstitution.code;
      if (!institutionCode) {
        logger.error("Invalid institution code:", {
          selectedInstitution,
          formDataPaymentInstitutionId: formData.paymentInstitutionId,
          safePaymentInstitutionsCount: safePaymentInstitutions.length,
        });
        messageHandler.showApiMessage(
          { message: "Invalid payment institution. Please select a valid payment institution." },
          "validation"
        );
        return;
      }

      // Validate account number
      const validation = validateAccountNumber(formData.accountNumber, institutionCode, selectedInstitutionType);
      if (!validation.isValid) {
        messageHandler.showApiMessage({ message: validation.error }, "validation");
        return;
      }

      // Validate currency is provided (required field)
      if (!formData.currency || formData.currency.trim() === "") {
        const fallbackCurrency = getCountryCurrencyCode(selectedCountry);
        if (!fallbackCurrency) {
          messageHandler.showApiMessage({ message: "Currency is required. Please select a currency." }, "validation");
          return;
        }
      }

      // Ensure we have a currency code (3-letter code as string)
      const currencyCode = formData.currency || getCountryCurrencyCode(selectedCountry);
      if (!currencyCode || currencyCode.trim() === "") {
        messageHandler.showApiMessage({ message: "Currency is required. Please select a currency." }, "validation");
        return;
      }

      // Build payload based on whether we're creating or updating
      let payload;
      let result;

      // Resolve country code (2-letter ISO code or country name code)
      const selectedCountryObj = findCountryDetails(selectedCountry || formData.country);
      const countryCodeValue = selectedCountryObj?.iso_code || selectedCountryObj?.isoCode || selectedCountryObj?.code || selectedCountryObj?.countryCode || selectedCountry || formData.country;

      if (existingData) {
        // For updates (PATCH), only mutable contact and business fields are allowed
        // countryCode, currency, paymentInstitutionCode, and accountNumber are immutable
        payload = {
          businessName: formData.businessName,
          primaryContactEmail: formData.primaryContactEmail,
          primaryContactName: formData.primaryContactName,
          primaryContactPhone: formData.primaryContactPhone,
          primary: formData.primary,
        };

        if (formData.description && formData.description.trim() !== "") {
          payload.description = formData.description.trim();
        }

        // Get organization ID and payment setup ID for update
        const currentUser = getUser();
        const organizationId = currentUser?.organizationId || currentUser?.organization?.id;
        const paymentSetupId = existingData.id || existingData.paymentSetupId;

        if (!organizationId || !paymentSetupId) {
          messageHandler.showApiMessage(
            { message: "Missing organization ID or payment setup ID for update" },
            "validation"
          );
          return;
        }

        const updateResponse = await paymentService.updatePaymentSetup(organizationId, paymentSetupId, payload);
        if (updateResponse && (updateResponse.status === 200 || updateResponse.status === 204)) {
          result = updateResponse.data || updateResponse;
        } else {
          throw new Error(updateResponse?.message || "Failed to update payment setup");
        }
      } else {
        // Resolve paymentInstitutionType: prioritize dynamically fetched paymentInstitutionType from API
        const paymentInstitutionType =
          selectedInstitutionType?.code ||
          resolvePaymentInstitutionType(selectedInstitution, _paymentMethodType);

        // For creates (POST), pass countryCode and paymentInstitutionType in the payload
        payload = {
          paymentInstitutionCode: institutionCode,
          paymentInstitutionType: paymentInstitutionType,
          businessName: formData.businessName,
          accountNumber: formData.accountNumber,
          primaryContactEmail: formData.primaryContactEmail,
          primaryContactName: formData.primaryContactName,
          primaryContactPhone: formData.primaryContactPhone,
          currency: currencyCode.trim(), // Pass 3-letter currency code as string (required)
          primary: formData.primary,
          countryCode: countryCodeValue,
        };

        // Only include description if it has a value (after trimming)
        if (formData.description && formData.description.trim() !== "") {
          payload.description = formData.description.trim();
        }

        logger.log(
          "POST Payload:",
          payload,
          "Institution Code:",
          institutionCode,
          "Selected Institution:",
          selectedInstitution
        );
        result = await submitPaymentSetup(payload);
      }

      if (result) {
        const defaultMsg = existingData
          ? "Payment setup updated successfully"
          : "Payment setup completed successfully";
        messageHandler.showApiMessage(
          { message: result?.message || defaultMsg, status: 200 },
          existingData ? "update payment setup" : "create payment setup"
        );
        onComplete();
      } else {
        messageHandler.showApiMessage(
          { message: existingData ? "Payment setup update failed" : "Payment setup failed" },
          existingData ? "update payment setup" : "create payment setup"
        );
      }
    } catch (error) {
      messageHandler.showApiMessage(error, "payment setup");
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      // If paymentInstitutionId changes, update payment method type & re-validate account number if present
      if (field === "paymentInstitutionId" && value) {
        const institution = safePaymentInstitutions.find(inst => inst.id.toString() === value);
        let matchedType = selectedInstitutionType;
        if (institution) {
          const pmType = getPaymentMethodTypeFromInstitution(institution);
          updated.paymentMethod = pmType;

          // Auto-match institution type if available in paymentInstitutionTypes
          if (paymentInstitutionTypes.length > 0) {
            const rawType = (institution.type || institution.institutionType || "").toLowerCase();
            const rawName = (institution.name || "").toLowerCase();
            const candidate = paymentInstitutionTypes.find(t => {
              const code = (t.code || "").toLowerCase();
              return (
                (rawType && (code === rawType || t.name?.toLowerCase() === rawType)) ||
                (institution.paymentInstitutionTypeId && t.id === institution.paymentInstitutionTypeId) ||
                (code.includes("momo") && (rawType.includes("momo") || rawName.includes("mtn") || rawName.includes("telecel"))) ||
                (code.includes("paybill") && pmType === "paybill") ||
                (code.includes("till") && pmType === "till")
              );
            });
            if (candidate) {
              matchedType = candidate;
              setSelectedInstitutionType(candidate);
            }
          }
        }
        if (updated.accountNumber) {
          const validation = validateAccountNumber(updated.accountNumber, institution?.code, matchedType);
          setAccountNumberError(validation.isValid ? "" : validation.error);
        }
      }

      // Validate account number field in real-time (only if value exists)
      if (field === "accountNumber" && value) {
        const currentInst = safePaymentInstitutions.find(inst => inst.id.toString() === updated.paymentInstitutionId);
        const instCode = currentInst?.code;
        const validation = validateAccountNumber(value, instCode, selectedInstitutionType);
        if (validation.isValid) {
          setAccountNumberError("");
        } else {
          setAccountNumberError(validation.error);
        }
      } else if (field === "accountNumber" && !value) {
        // Clear error when field is empty
        setAccountNumberError("");
      }

      return updated;
    });
  };

  const handleCountryCurrencyChange = pairId => {
    userSelectedCountryRef.current = true; // User has explicitly chosen; disable auto-select
    const pair = countryCurrencyPairs.find(p => p.id === pairId);
    if (!pair) return;

    setSelectedCountry(pair.countryName);
    setFormData(prev => ({
      ...prev,
      country: pair.countryName,
      countryCode: pair.countryCode,
      currency: pair.currencyCode,
      paymentInstitutionId: "",
    }));

    // Clear institutions to force reload with new context
    setPaymentInstitutions([]);
    loadPaymentInstitutions(pair.currencyCode, pair.countryName);
  };

  // Helper to determine if an institution is mobile money/wallet
  const isWalletInstitution = useCallback(inst => {
    if (!inst) return false;
    const rawType = (
      inst.paymentInstitutionType ||
      inst.type ||
      inst.paymentChannelType ||
      ""
    ).toLowerCase();
    const gateway = (inst.gateway || "").toLowerCase();
    const name = (inst.name || "").toLowerCase();
    return (
      rawType === "mobile_money" ||
      rawType === "wallet" ||
      rawType.includes("momo") ||
      gateway.includes("mpesa") ||
      gateway.includes("m-pesa") ||
      name.includes("mpesa") ||
      name.includes("m-pesa") ||
      name.includes("airtel") ||
      name.includes("mtn") ||
      name.includes("tigo") ||
      name.includes("telecel") ||
      name.includes("vodafone") ||
      name.includes("orange")
    );
  }, []);

  // Helper to determine if an institution type is mobile money/wallet
  const isMobileMoneyType = useCallback(typeObj => {
    if (!typeObj) return false;
    const code = (typeObj.code || "").toLowerCase();
    const name = (typeObj.name || "").toLowerCase();
    return (
      code.includes("mobile_money") ||
      code.includes("momo") ||
      code.includes("wallet") ||
      code.includes("mpesa") ||
      name.includes("mobile money") ||
      name.includes("momo") ||
      name.includes("wallet") ||
      name.includes("m-pesa") ||
      name.includes("mpesa")
    );
  }, []);

  // Filter institutions based on selected institution type
  const filteredPaymentInstitutions = useMemo(() => {
    if (!safePaymentInstitutions.length) return [];
    if (!selectedInstitutionType) return safePaymentInstitutions;

    const isWallet = isMobileMoneyType(selectedInstitutionType);
    const typeCode = (selectedInstitutionType.code || "").toLowerCase();

    const filtered = safePaymentInstitutions.filter(inst => {
      // 1. Direct type match if present
      const instType = (
        inst.paymentInstitutionType ||
        inst.type ||
        inst.paymentChannelType ||
        ""
      ).toLowerCase();
      if (instType && typeCode && instType === typeCode) {
        return true;
      }

      // 2. Mobile money vs Bank separation
      if (isWallet) {
        return isWalletInstitution(inst);
      } else {
        return !isWalletInstitution(inst);
      }
    });

    return filtered.length > 0 ? filtered : safePaymentInstitutions;
  }, [safePaymentInstitutions, selectedInstitutionType, isMobileMoneyType, isWalletInstitution]);

  const handleSelectInstitutionType = useCallback(
    typeObj => {
      setSelectedInstitutionType(typeObj);

      // If currently selected institution doesn't match the new type, reset it
      if (formData.paymentInstitutionId) {
        const currentInst = safePaymentInstitutions.find(
          inst => inst.id?.toString() === formData.paymentInstitutionId?.toString()
        );
        if (currentInst) {
          const currentIsWallet = isWalletInstitution(currentInst);
          const targetIsWallet = isMobileMoneyType(typeObj);
          if (currentIsWallet !== targetIsWallet) {
            setFormData(prev => ({ ...prev, paymentInstitutionId: "" }));
          }
        }
      }

      if (formData.accountNumber) {
        const currentInst = safePaymentInstitutions.find(
          inst => inst.id?.toString() === formData.paymentInstitutionId?.toString()
        );
        const validation = validateAccountNumber(formData.accountNumber, currentInst?.code, typeObj);
        setAccountNumberError(validation.isValid ? "" : validation.error);
      }
    },
    [
      formData.paymentInstitutionId,
      formData.accountNumber,
      safePaymentInstitutions,
      isWalletInstitution,
      isMobileMoneyType,
      validateAccountNumber,
    ]
  );

  const paymentMethodType = getPaymentMethodType();
  const selectedInstitution = safePaymentInstitutions.find(
    inst => inst.id.toString() === formData.paymentInstitutionId
  );

  // For edit mode, also try to find institution from all loaded institutions
  const editModeInstitution =
    existingData && !selectedInstitution && paymentInstitutionsCount > 0
      ? safePaymentInstitutions.find(inst => inst.id.toString() === getExistingValue("paymentInstitutionId"))
      : null;

  const displayInstitution = selectedInstitution || editModeInstitution;

  return (
    <div className={borderless ? "" : "bg-white rounded-lg shadow-sm border"}>
      {/* Header */}
      {borderless ? (
        <div className="flex items-center gap-3 pb-4 mb-6 border-b border-gray-100">
          {onPrevious && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onPrevious}
              className="h-8 w-8 text-gray-500 hover:text-gray-900 cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {existingData ? "Edit Payment Method" : "Add Payment Method"}
            </h3>
            <p className="text-sm text-gray-500">
              {existingData ? "Update your payout method details" : "Configure your account for receiving payouts"}
            </p>
          </div>
        </div>
      ) : (
        <div className="px-6 py-4 border-b">
          <h1 className="text-xl font-semibold">Payment Setup</h1>
          <div className="w-full bg-gray-200 h-1 mt-2">
            <div className="bg-black h-1 w-full"></div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className={borderless ? "" : "p-6"}>
        {!borderless && (
          <div className="mb-6">
            <h2 className="text-lg font-medium mb-2">Payment Setup</h2>
            <p className="text-gray-600">Set up your payment method for receiving payments</p>
          </div>
        )}

        {/* Shimmer skeleton — shown inside the settings card while loading */}
        {borderless && countriesLoading ? (
          <div className="space-y-5 animate-pulse">
            <div>
              <div className="h-3 w-32 bg-slate-200 rounded mb-2" />
              <div className="h-10 w-full bg-slate-200 rounded-lg" />
            </div>
            <div>
              <div className="h-3 w-40 bg-slate-200 rounded mb-2" />
              <div className="h-10 w-full bg-slate-200 rounded-lg" />
            </div>
            <div>
              <div className="h-3 w-36 bg-slate-200 rounded mb-2" />
              <div className="h-10 w-full bg-slate-200 rounded-lg" />
            </div>
            <div>
              <div className="h-3 w-28 bg-slate-200 rounded mb-2" />
              <div className="h-10 w-full bg-slate-200 rounded-lg" />
            </div>
            <div className="flex justify-end pt-2">
              <div className="h-10 w-36 bg-slate-200 rounded-lg" />
            </div>
          </div>
        ) : (
          <>
        {/* Country & Currency Selection */}
        <div className="mb-6">
          <Label className="block text-sm font-medium mb-2">
            Country & Currency <span className="text-red-500">*</span>
          </Label>
          <Select
            value={
              formData.country && formData.currency
                ? `${countryCurrencyPairs.find(p => p.countryName === formData.country && p.currencyCode === formData.currency)?.id || ""}`
                : ""
            }
            onValueChange={handleCountryCurrencyChange}
            disabled={countriesLoading || !!existingData}
            required
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={countriesLoading ? "Loading countries..." : "Select Country & Currency"} />
            </SelectTrigger>
            <SelectContent>
              {countryCurrencyPairs.map(pair => (
                <SelectItem key={pair.id} value={pair.id}>
                  {pair.label}
                </SelectItem>
              ))}
              {countryCurrencyPairs.length === 0 && !countriesLoading && (
                <div className="p-2 text-center text-sm text-gray-500">No options available</div>
              )}
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500 mt-1">Select the country and currency for this payment method</p>
        </div>

        {/* 1. Payment Institution Type Selection (Country -> Institution Type) */}
        {(formData.country || existingData) && (
          <div className="mb-6">
            <Label className="block text-sm font-medium mb-2">
              Payment Institution Type <span className="text-red-500">*</span>
            </Label>
            <Select
              value={selectedInstitutionType ? selectedInstitutionType.id?.toString() || selectedInstitutionType.code : ""}
              onValueChange={val => {
                const found = paymentInstitutionTypes.find(
                  t => (t.id?.toString() || t.code) === val
                );
                if (found) {
                  handleSelectInstitutionType(found);
                }
              }}
              disabled={paymentInstitutionTypesLoading || !!existingData}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={
                    paymentInstitutionTypesLoading
                      ? "Loading institution types..."
                      : "Select payment institution type"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {paymentInstitutionTypes.map(t => {
                  const valKey = t.id?.toString() || t.code;
                  const lengthSuffix =
                    t.minAccountNumberLength != null && t.maxAccountNumberLength != null
                      ? t.minAccountNumberLength === t.maxAccountNumberLength
                        ? ` (${t.minAccountNumberLength} digits)`
                        : ` (${t.minAccountNumberLength}–${t.maxAccountNumberLength} digits)`
                      : "";

                  return (
                    <SelectItem key={valKey} value={valKey}>
                      {t.name}{lengthSuffix}
                    </SelectItem>
                  );
                })}
                {paymentInstitutionTypes.length === 0 && !paymentInstitutionTypesLoading && (
                  <div className="p-2 text-center text-sm text-gray-500">
                    No institution types available for this country
                  </div>
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              Select whether you are setting up a Bank Account or Mobile Money / Wallet
            </p>
          </div>
        )}

        {/* 2. Payment Institution Selection - Show when currency is selected or in edit mode */}
        {(formData.currency || existingData) && (
          <div className="mb-6">
            <Label className="block text-sm font-medium mb-2">
              Payment Institution <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.paymentInstitutionId}
              onValueChange={value => handleInputChange("paymentInstitutionId", value)}
              disabled={paymentInstitutionsLoading || (!formData.currency && !existingData) || !!existingData}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={paymentInstitutionsLoading ? "Loading institutions..." : "Select payment institution"}
                />
              </SelectTrigger>
              <SelectContent>
                {filteredPaymentInstitutions.map(institution => (
                  <SelectItem key={institution.id} value={institution.id.toString()}>
                    {institution.name} {institution.code ? `(${institution.code})` : ""}
                  </SelectItem>
                ))}
                {filteredPaymentInstitutions.length === 0 && !paymentInstitutionsLoading && (
                  <div className="p-2 text-center text-sm text-gray-500">
                    No payment institutions available for this selection
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Payment Method Details */}
        {/* Show fields immediately in edit mode, or when we have institution selected */}
        {(existingData || displayInstitution) && (
          <div className="mb-6">
            {displayInstitution && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <p className="text-sm text-blue-800">
                    Selected: {displayInstitution.name} (
                    {paymentMethodType === "bank"
                      ? "Bank Account"
                      : paymentMethodType === "paybill"
                        ? "M-Pesa Paybill"
                        : "M-Pesa Till"}
                    )
                  </p>
                </div>
              </div>
            )}

            {/* Account Number */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <Label className="block text-sm font-medium">
                  Account Number <span className="text-red-500">*</span>
                </Label>
                {selectedInstitutionType && selectedInstitutionType.maxAccountNumberLength && (
                  <span className="text-xs text-slate-500">
                    Max: {selectedInstitutionType.maxAccountNumberLength} characters
                  </span>
                )}
              </div>
              <Input
                type="text"
                maxLength={selectedInstitutionType?.maxAccountNumberLength || undefined}
                placeholder={
                  selectedInstitutionType && selectedInstitutionType.minAccountNumberLength
                    ? `Enter ${selectedInstitutionType.name} account (${selectedInstitutionType.minAccountNumberLength}–${selectedInstitutionType.maxAccountNumberLength} digits)`
                    : "Enter account number"
                }
                className={`w-full ${accountNumberError ? "border-red-500" : ""}`}
                value={formData.accountNumber}
                onChange={e => handleInputChange("accountNumber", e.target.value)}
                disabled={!!existingData}
              />
              {selectedInstitutionType && selectedInstitutionType.minAccountNumberLength != null && (
                <div className="flex items-center justify-between text-xs text-slate-500 mt-1">
                  <span>
                    Allowed length: {selectedInstitutionType.minAccountNumberLength} to {selectedInstitutionType.maxAccountNumberLength} digits
                  </span>
                  <span
                    className={
                      formData.accountNumber.length >= selectedInstitutionType.minAccountNumberLength &&
                      formData.accountNumber.length <= selectedInstitutionType.maxAccountNumberLength
                        ? "text-emerald-600 font-semibold"
                        : formData.accountNumber.length > 0
                        ? "text-amber-600 font-medium"
                        : "text-slate-400"
                    }
                  >
                    {formData.accountNumber.length} / {selectedInstitutionType.maxAccountNumberLength}
                  </span>
                </div>
              )}
              {accountNumberError && <p className="text-sm text-red-500 mt-1">{accountNumberError}</p>}
            </div>

            {/* Business Name */}
            <div className="mb-4">
              <Label className="block text-sm font-medium mb-2">
                Business Name <span className="text-red-500">*</span>
              </Label>
              <Input
                type="text"
                placeholder="Enter business name"
                className="w-full"
                value={formData.businessName}
                onChange={e => handleInputChange("businessName", e.target.value)}
              />
            </div>

            {/* Description */}
            <div className="mb-4">
              <Label className="block text-sm font-medium mb-2">
                Description <span className="text-red-500">*</span>
              </Label>
              <Input
                type="text"
                placeholder="Enter description"
                className="w-full"
                value={formData.description}
                onChange={e => handleInputChange("description", e.target.value)}
              />
            </div>

            {/* Percentage Charge removed */}
          </div>
        )}

        {/* Contact Information */}
        {/* Show contact info immediately in edit mode, or when we have institution selected */}
        {(existingData || displayInstitution) && (
          <div className="mb-6">
            <h3 className="font-medium mb-4">Contact Information</h3>
            <p className="text-gray-600 mb-4">Please provide your business contact details for payment processing</p>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="block text-sm font-medium mb-2">
                  Primary Contact Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="text"
                  placeholder="Enter primary contact name"
                  className="w-full"
                  value={formData.primaryContactName}
                  onChange={e => handleInputChange("primaryContactName", e.target.value)}
                />
              </div>
              <div>
                <Label className="block text-sm font-medium mb-2">
                  Primary Contact Phone <span className="text-red-500">*</span>
                </Label>
                <PhoneInput
                  defaultCountry="KE"
                  value={formData.primaryContactPhone}
                  onChange={value => handleInputChange("primaryContactPhone", value || "")}
                  placeholder="Enter primary contact phone"
                  className="w-full"
                />
              </div>
            </div>

            <div className="mb-4">
              <Label className="block text-sm font-medium mb-2">
                Primary Contact Email <span className="text-red-500">*</span>
              </Label>
              <Input
                type="email"
                placeholder="Enter primary contact email"
                className="w-full"
                value={formData.primaryContactEmail}
                onChange={e => handleInputChange("primaryContactEmail", e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Primary Payment Method Toggle */}
        {(existingData || displayInstitution) && (
          <div className="mb-6">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="primary"
                checked={formData.primary}
                onCheckedChange={checked => handleInputChange("primary", checked === true)}
              />
              <Label
                htmlFor="primary"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                Set as primary payment method
              </Label>
            </div>
            <p className="text-sm text-gray-500 mt-2 ml-6">
              Primary payment methods are used by default for transactions
            </p>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between">
          {onPrevious && (
            <Button
              variant="ghost"
              onClick={onPrevious}
              className="flex items-center text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            disabled={loading || !formData.paymentInstitutionId || !formData.currency}
            className="flex items-center"
          >
            {loading ? "Processing..." : existingData ? "Update Payment Method" : "Complete Setup"}
          </Button>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSetupForm;
